import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";

import DashboardLayout from "./components/Layout";
import useRole from "./components/useRole";
import { getVouchers, deleteVoucher } from "../../api/vouchers";

const VouchersPage = () => {
  const { eventId } = useParams();
  const { isAdmin } = useRole(eventId);
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    // the role on this event, which may differ from the one held on others
    if (!isAdmin) {
      window.location.replace("/");
    }
  }, [isAdmin]);

  const fetchVouchers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getVouchers({
        search: searchTerm,
        isActive: statusFilter === "all" ? undefined : statusFilter === "active",
        eventId: eventId
      });
      setVouchers(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching vouchers:", error);
    } finally {
      setLoading(false);
    }
  }, [eventId, searchTerm, statusFilter]);

  useEffect(() => {
    fetchVouchers();
  }, [fetchVouchers]);

  const handleDeleteVoucher = async (voucherId) => {
    if (window.confirm("Bu voucher-u silmək istədiyinizə əminsiniz?")) {
      try {
        await deleteVoucher(voucherId);
        fetchVouchers(); // Refresh the list
      } catch (error) {
        console.error("Error deleting voucher:", error);
        alert("Voucher silinərkən xəta baş verdi");
      }
    }
  };

  const getStatusBadge = (voucher) => {
    const now = new Date();
    const validFrom = new Date(voucher.validFrom);
    const validUntil = new Date(voucher.validUntil);

    if (!voucher.isActive) {
      return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">Deaktiv</span>;
    }

    if (now < validFrom) {
      return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Gələcək</span>;
    }

    if (now > validUntil) {
      return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Müddəti bitib</span>;
    }

    if (voucher.usageLimit && voucher.usedCount >= voucher.usageLimit) {
      return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Limit dolub</span>;
    }

    return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Aktiv</span>;
  };

  const getDiscountText = (voucher) => {
    if (voucher.discountType === "percentage") {
      return `${voucher.discountValue}% endirim`;
    } else {
      return `${voucher.discountValue}₼ endirim`;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("az-AZ", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };
  if (!isAdmin) return <Navigate to={`/dashboard/${eventId}`} replace />;


  return (
    <DashboardLayout id={eventId}>
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Voucher İdarəetməsi</h1>
          <Link
            to={`/dashboard/${eventId}/vouchers/new`}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            ➕ Yeni Voucher
          </Link>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Voucher kodu və ya adı ilə axtar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Bütün statuslar</option>
                <option value="active">Aktiv</option>
                <option value="inactive">Deaktiv</option>
              </select>
            </div>
          </div>
        </div>

        {/* Vouchers Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Yüklənir...</p>
            </div>
          ) : vouchers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>Hələ heç bir voucher yoxdur</p>
              <Link
                to={`/dashboard/${eventId}/vouchers/new`}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                İlk voucher-u yaradın
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Voucher
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Endirim
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      İstifadə
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Müddət
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Əməliyyatlar
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {vouchers.map((voucher) => (
                    <tr key={voucher._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {voucher.code}
                          </div>
                          <div className="text-sm text-gray-500">
                            {voucher.name?.az || voucher.name?.en || "Ad yoxdur"}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {getDiscountText(voucher)}
                        </div>
                        {voucher.minOrderAmount > 0 && (
                          <div className="text-xs text-gray-500">
                            Min: {voucher.minOrderAmount}₼
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {voucher.usedCount} / {voucher.usageLimit || "∞"}
                        </div>
                        {voucher.usageLimit && (
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{
                                width: `${Math.min((voucher.usedCount / voucher.usageLimit) * 100, 100)}%`
                              }}
                            ></div>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div>{formatDate(voucher.validFrom)}</div>
                        <div>→ {formatDate(voucher.validUntil)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(voucher)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <Link
                            to={`/dashboard/${eventId}/vouchers/${voucher._id}`}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Düzəlt
                          </Link>
                          <button
                            onClick={() => handleDeleteVoucher(voucher._id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Sil
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default VouchersPage;
